/**
 * Playground game-session-token (GST) issuance for the SDK's Firebase-backed
 * cloud-run callers.
 *
 * The playground emulates the H5 host, so — like the native/web host — it must
 * mint a GST and attach `x-h5-game-session` on H5 backend RPCs. Without it,
 * enforce-mode surfaces (e.g. `game-server:h5-leaderboard`, which guards
 * `/h5/simulation/recipe`) return `403 game_session_required`.
 *
 * Mirrors `client/services/h5GameSession`: mint via `POST /v1/game-sessions/mint`
 * with the user's Firebase ID token, cache token + expiry keyed by uid, re-mint
 * on the expiry lead, and treat `{ disabled: true }` issuance as "no token"
 * (never blocking). Keying on uid matters because the playground can switch the
 * signed-in emulator user in-tab without a reload (signInWithLocalPlayer), so a
 * process-lifetime cache must not attach one user's token to another's request.
 *
 * PLAYGROUND-ONLY: reached only from `callRemoteFunction.ts`, which is itself
 * playground/Firebase-gated.
 */

import { getPlaygroundConfig, getCloudRunUrl } from '../config/playground'
import { getFirebaseClient } from '../firebase/firebaseClient'

const REFRESH_LEAD_MS = 5 * 60 * 1000
const DISABLED_RECHECK_MS = 5 * 60 * 1000
const FAILURE_BACKOFF_MS = 30 * 1000

export interface GameSessionMintDeps {
  getIdToken: () => Promise<string>
  getCloudRunUrl: () => string
  gameId: string
  uid: string
  versionTag?: string
}

type MintOutcome =
  | { disabled: true }
  | { token: string; expiresAt: number }
  | null

// A persistent mint failure re-mints on the 30s backoff and every RPC calls
// getHeaderToken, so warnings MUST be deduplicated or they flood the console.
// Keyed by identity + status + body-prefix so a *different* failure still surfaces.
const warnedMintFailures = new Set<string>()

function describeMintFailure(status: number, body: string): string {
  if (status === 401) {
    return 'You are not signed in to the playground — sign in via the toolbar so the SDK can mint a game-session token.'
  }
  if (status === 404) {
    // Newer servers return a machine-readable `code`; older ones only prose —
    // fall back to body-text matching when there is no code.
    let code: string | undefined
    try {
      const parsed = JSON.parse(body) as { code?: unknown }
      if (typeof parsed.code === 'string') code = parsed.code
    } catch {
      // Not JSON — fall through to text matching.
    }
    if (code === 'VERSION_TAG_NOT_FOUND' || (!code && /version tag/i.test(body))) {
      return "This game has no 'private' server-config version. Sign in via the toolbar if you have not; if you are already signed in, edit a server-config file (a no-op re-save is skipped) to force a fresh upload, then reload."
    }
    if (code === 'GAME_NOT_FOUND' || (!code && /game not found/i.test(body))) {
      return 'This game is not registered in the playground backend.'
    }
  }
  return `Unexpected mint response (HTTP ${status}).`
}

function warnMintFailureOnce(gameId: string, status: number, body: string): void {
  const key = `${gameId}:${status}:${body.slice(0, 60)}`
  if (warnedMintFailures.has(key)) return
  warnedMintFailures.add(key)
  console.warn(
    `[RUN:playground] Could not mint a game-session token (HTTP ${status}). ` +
      describeMintFailure(status, body) +
      ' Until this is resolved, enforce-mode H5 backend surfaces (recipe, leaderboard, LLM) will return 403 game_session_required.',
  )
}

async function mintOnce(deps: GameSessionMintDeps): Promise<MintOutcome> {
  const idToken = await deps.getIdToken()
  const baseUrl = deps.getCloudRunUrl().replace(/\/$/, '')
  const body = deps.versionTag
    ? { gameId: deps.gameId, versionTag: deps.versionTag }
    : { gameId: deps.gameId }

  const response = await fetch(`${baseUrl}/v1/game-sessions/mint`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    warnMintFailureOnce(deps.gameId, response.status, body)
    return null
  }

  const text = await response.text()
  let data: { disabled?: unknown; token?: unknown; expiresAt?: unknown }
  try {
    data = JSON.parse(text)
  } catch {
    return null
  }

  if (data.disabled === true) return { disabled: true }
  if (typeof data.token === 'string' && typeof data.expiresAt === 'number') {
    // Cloud-run returns JWT `exp` (epoch seconds); store epoch ms so expiry
    // math compares against the injected `now`.
    return { token: data.token, expiresAt: data.expiresAt * 1000 }
  }
  return null
}

function identityKey(gameId: string, uid: string): string {
  // NUL separator can't appear in a Firebase uid or gameId, so this is injective.
  return `${gameId}\0${uid}`
}

export class PlaygroundGameSessionHolder {
  // All mint state (token, backoff, in-flight mint) belongs to one identity —
  // the current `{ gameId, uid }`. GSTs are both user- and game-bound (the
  // server checks the token's `gid` against the request appId and its `sub`
  // against the auth uid), and the playground can switch the signed-in emulator
  // user in-tab without a reload. When the identity changes we abandon the prior
  // identity's token AND its backoff, so one user's failure can't suppress
  // another's mint. Mirrors the client store's `{ appId, uid }` keying.
  private key: string | null = null
  private slot: { token: string; expiresAt: number } | null = null
  private mutedUntil = 0
  private inFlight: Promise<void> | null = null

  async getHeaderToken(
    deps: GameSessionMintDeps,
    now: number = Date.now(),
  ): Promise<string | null> {
    const { gameId, uid } = deps
    if (!gameId || !uid) return null
    const key = identityKey(gameId, uid)

    if (this.key !== key) {
      // Identity changed — orphan everything tied to the previous identity,
      // including any in-flight mint (its result is discarded in refresh()).
      this.key = key
      this.slot = null
      this.mutedUntil = 0
      this.inFlight = null
    }

    // Fresh, outside the lead window — reuse without minting.
    if (this.slot && this.slot.expiresAt - now > REFRESH_LEAD_MS) {
      return this.slot.token
    }

    // Issuance muted (disabled/failed recently) for THIS identity — serve a
    // still-valid held token, but don't hammer the mint endpoint.
    if (now < this.mutedUntil) {
      return this.slot && this.slot.expiresAt > now ? this.slot.token : null
    }

    if (!this.inFlight) {
      const p: Promise<void> = this.refresh(deps, now, key).finally(() => {
        if (this.inFlight === p) this.inFlight = null
      })
      this.inFlight = p
    }
    await this.inFlight

    // Identity may have flipped while we awaited; a stale result isn't ours.
    if (this.key !== key) return null
    return this.slot && this.slot.expiresAt > now ? this.slot.token : null
  }

  private async refresh(deps: GameSessionMintDeps, now: number, key: string): Promise<void> {
    let outcome: MintOutcome
    try {
      outcome = await mintOnce(deps)
    } catch {
      outcome = null
    }

    // Identity changed mid-flight — this mint is for a stale identity; discard.
    if (this.key !== key) return

    if (outcome && 'token' in outcome) {
      this.slot = { token: outcome.token, expiresAt: outcome.expiresAt }
      this.mutedUntil = 0
      return
    }
    if (outcome && 'disabled' in outcome) {
      // Recheck when issuance may be back — but no later than the held token's
      // own expiry, so an expired token re-mints promptly (mirrors the client).
      const heldValid = this.slot && this.slot.expiresAt > now
      this.mutedUntil = heldValid
        ? Math.min(now + DISABLED_RECHECK_MS, this.slot!.expiresAt)
        : now + DISABLED_RECHECK_MS
      return
    }
    // Non-OK / malformed / network error — brief backoff so we don't mint per RPC.
    this.mutedUntil = now + FAILURE_BACKOFF_MS
  }
}

let holder: PlaygroundGameSessionHolder | null = null

export function getPlaygroundGameSessionHolder(): PlaygroundGameSessionHolder {
  if (!holder) holder = new PlaygroundGameSessionHolder()
  return holder
}

/**
 * Shared playground GST accessor. Returns the current game-session token for the
 * signed-in playground user, or `null` when playground mode is off or issuance
 * is unavailable. Every Firebase-coupled host client (and `callRemoteFunction`)
 * routes through this so there is one place that mints + keys the GST.
 *
 * Fails open: any throw (e.g. `getFirebaseClient()` rejecting) resolves to `null`
 * so a GST hiccup never breaks a backend call — the receiver's mode decides
 * whether a missing header blocks (Req 7).
 */
export async function getPlaygroundGameSessionToken(): Promise<string | null> {
  try {
    const config = getPlaygroundConfig()
    if (!config) return null
    const client = await getFirebaseClient()
    return await getPlaygroundGameSessionHolder().getHeaderToken({
      getIdToken: () => client.getIdTokenOrThrow(),
      getCloudRunUrl,
      gameId: config.gameId,
      uid: client.getProfileId?.() ?? '',
      // Always mint under 'private', even before any rundot/*.config.json has
      // been uploaded (when config.versionTag is still undefined). The server
      // treats 'private' as a developing tag and skips the version-existence
      // check, so the first-run mint for a registered game always succeeds —
      // minting without a tag would default to 'public' and 404.
      versionTag: 'private',
    })
  } catch {
    return null
  }
}
